import {
  createContext,
  useContext,
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";

// Sidebar width is driven by a single CSS variable (--bs-sidebar-width) that the
// sidebar, header and main content all read. We override it inline on <html> so
// resizing/collapsing shifts the whole layout together, and persist the user's
// choice so it survives reloads.
const STORAGE_KEY = "sidebar:v1";
const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 170; // smallest resized width
const MAX_WIDTH = 480;
// Collapse only when dragged well past the minimum, not at it. Without this dead
// zone, grabbing the handle while already at MIN_WIDTH collapses on the first
// sub-pixel move and you can never drag rightward to grow it again.
const COLLAPSE_THRESHOLD = 110;

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        width: clamp(Number(p.width) || DEFAULT_WIDTH, MIN_WIDTH, MAX_WIDTH),
        collapsed: !!p.collapsed,
      };
    }
  } catch {
    // ignore malformed storage
  }
  return { width: DEFAULT_WIDTH, collapsed: false };
};

const SidebarContext = createContext(null);

export const SidebarProvider = ({ children }) => {
  const [{ width, collapsed }, setState] = useState(loadInitial);
  const [isResizing, setIsResizing] = useState(false);

  const setCollapsed = useCallback(
    (value) =>
      setState((s) => ({
        ...s,
        collapsed: typeof value === "function" ? value(s.collapsed) : value,
      })),
    []
  );

  // Persist width + collapsed.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ width, collapsed }));
    } catch {
      // storage may be unavailable (private mode) — non-fatal
    }
  }, [width, collapsed]);

  // Apply before paint so a persisted state doesn't flash the default first.
  // The panel keeps its real width and slides off-screen when collapsed; the
  // layout margin (--bs-sidebar-width) drops to 0 so content reclaims the space.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--sidebar-panel-width", `${width}px`);
    root.style.setProperty(
      "--bs-sidebar-width",
      collapsed ? "0px" : `${width}px`
    );
  }, [width, collapsed]);

  const startResize = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.classList.add("is-resizing-sidebar");

    const onMove = (ev) => {
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      // The sidebar is pinned to the left edge, so the pointer's X is the width.
      if (x < COLLAPSE_THRESHOLD) {
        // Dragged well past the minimum → collapse and stop tracking.
        setState((s) => ({ ...s, collapsed: true }));
        end();
        return;
      }
      // Between the threshold and the minimum the width just pins to MIN_WIDTH,
      // so dragging outward from the minimum always grows instead of collapsing.
      setState((s) => ({
        ...s,
        collapsed: false,
        width: clamp(x, MIN_WIDTH, MAX_WIDTH),
      }));
    };

    const end = () => {
      setIsResizing(false);
      document.body.classList.remove("is-resizing-sidebar");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", end);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", end);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", end);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", end);
  }, []);

  const collapse = useCallback(() => setCollapsed(true), [setCollapsed]);
  const expand = useCallback(() => setCollapsed(false), [setCollapsed]);
  const toggleCollapse = useCallback(
    () => setCollapsed((c) => !c),
    [setCollapsed]
  );

  return (
    <SidebarContext.Provider
      value={{
        width,
        collapsed,
        isResizing,
        startResize,
        collapse,
        expand,
        toggleCollapse,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return ctx;
};
